const RAW_BASE = process.env.REACT_APP_API_URL || "";
const NORMALIZED_BASE = RAW_BASE.trim().replace(/\/+$/, "").replace(/\/api\/?$/, "");
const BASE = RAW_BASE
  ? /^https?:\/\//i.test(NORMALIZED_BASE)
    ? NORMALIZED_BASE
    : /(localhost|127\.0\.0\.1|\[::1\])/i.test(NORMALIZED_BASE)
      ? `http://${NORMALIZED_BASE}`
      : `https://${NORMALIZED_BASE}`
  : "";

function buildUrl(path) {
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    return path.startsWith("/") ? path : `/${path}`;
  }
  if (!BASE) return path;
  return new URL(path, `${BASE}/`).toString();
}

/* ─── Helper: make authenticated fetch ── */
async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("th_token");
  const isFormData =
    typeof FormData !== "undefined" && options?.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers || {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const url = buildUrl(path);
  const res = await fetch(url, { ...options, headers });
  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
    return data;
  }

  const text = await res.text();
  if (!res.ok) throw new Error(text ? `Request failed (${res.status}): ${text}` : `Request failed (${res.status})`);
  throw new Error(`Expected JSON response but got ${contentType || "unknown content"}`);
}

/* ═══════════════════════════════════════
   AUTH
═══════════════════════════════════════ */
export const authApi = {
  register: (name, email, password) =>
    apiFetch("/api/auth/register", { method: "POST", body: JSON.stringify({ name, email, password }) }),

  login: (email, password) =>
    apiFetch("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),

  me: () => apiFetch("/api/auth/me"),
};

/* ═══════════════════════════════════════
   COURSES  ← /api prefix added to all
═══════════════════════════════════════ */
export const coursesApi = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
    ).toString();
    return apiFetch(`/api/courses${qs ? "?" + qs : ""}`);   // ✅ fixed
  },

  getById: (id) => apiFetch(`/api/courses/${id}`),           // ✅ fixed

  getVideos: (courseId) => apiFetch(`/api/courses/${courseId}/videos`),

  getReviews: (courseId) => apiFetch(`/api/courses/${courseId}/reviews`),  // ✅ fixed

  submitReview: (courseId, rating, comment) =>
    apiFetch(`/api/courses/${courseId}/reviews`, {           // ✅ fixed
      method: "POST",
      body: JSON.stringify({ rating, comment }),
    }),

  deleteReview: (courseId) =>
    apiFetch(`/api/courses/${courseId}/reviews`, { method: "DELETE" }),    // ✅ fixed
};

/* ═══════════════════════════════════════
   ENROLLMENTS
═══════════════════════════════════════ */
export const enrollmentsApi = {
  enroll: (course_id, payment_ref = null) =>
    apiFetch("/api/enrollments", { method: "POST", body: JSON.stringify({ course_id, payment_ref }) }),

  myEnrollments: () => apiFetch("/api/enrollments/my"),

  unenroll: (course_id) => apiFetch(`/api/enrollments/${course_id}`, { method: "DELETE" }),
};

/* ═══════════════════════════════════════
   PROGRESS
═══════════════════════════════════════ */
export const progressApi = {
  update: (course_id, lesson_number) =>
    apiFetch("/api/progress", { method: "PUT", body: JSON.stringify({ course_id, lesson_number }) }),

  get: (course_id) => apiFetch(`/api/progress/${course_id}`),
};

/* ═══════════════════════════════════════
   WISHLIST
═══════════════════════════════════════ */
export const wishlistApi = {
  getAll: () => apiFetch("/api/wishlist"),

  toggle: (course_id) =>
    apiFetch(`/api/wishlist/${course_id}`, { method: "POST" }),
};

/* ═══════════════════════════════════════
   ADMIN
═══════════════════════════════════════ */
export const adminApi = {
  stats: () => apiFetch("/api/admin/stats"),

  getCourses: () => apiFetch("/api/admin/courses"),

  getInstructors: () => apiFetch("/api/admin/course-instructors"),

  createCourse: (data) =>
    apiFetch("/api/admin/courses", { method: "POST", body: JSON.stringify(data) }),

  getCourseVideos: (courseId) =>
    apiFetch(`/api/admin/courses/${courseId}/videos`),

  addCourseVideo: (courseId, data) =>
    apiFetch(`/api/admin/courses/${courseId}/videos`, {
      method: "POST",
      body:
        typeof FormData !== "undefined" && data instanceof FormData
          ? data
          : JSON.stringify(data),
    }),

  updateCourseVideo: (courseId, videoId, data) =>
    apiFetch(`/api/admin/courses/${courseId}/videos/${videoId}`, {
      method: "PUT",
      body:
        typeof FormData !== "undefined" && data instanceof FormData
          ? data
          : JSON.stringify(data),
    }),

  deleteCourseVideo: (courseId, videoId) =>
    apiFetch(`/api/admin/courses/${courseId}/videos/${videoId}`, { method: "DELETE" }),

  updateCourse: (id, data) =>
    apiFetch(`/api/admin/courses/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  deleteCourse: (id) =>
    apiFetch(`/api/admin/courses/${id}`, { method: "DELETE" }),
};

/* ─── Token helpers ── */
export function saveToken(token) { localStorage.setItem("th_token", token); }
export function clearToken()     { localStorage.removeItem("th_token"); }
export function getToken()       { return localStorage.getItem("th_token"); }
